import React from "react";

interface TotalAchievementScore { revenueOfHomesClosed: string; overridePercentage: string; bonusPoolAvailableAt100: string; totalMaxAt120: string; }
interface BonusCalculationData { totalAchievementScore: TotalAchievementScore; totalScore: string; percentage: string; bonusPool: string; totalBonusPayout: string; }
interface BonusCalculationCM3PMSubFormProps {
  data: BonusCalculationData; isEditable: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text'; maxLength?: number; minLength?: number; max?: number; min?: number }) => void;
}

const BonusCalculationCM3PMSubForm: React.FC<BonusCalculationCM3PMSubFormProps> = ({ data, isEditable, onInputChange }) => {
  const fmt = (v: string) => v && v !== "0" && !isNaN(Number(v)) ? parseFloat(v).toLocaleString() : "";

  return (
    <div style={{ borderTop: "1px solid var(--border-section)" }}>
      {/* Section accent header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
        <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" as const, color: "var(--text-secondary)" }}>Bonus Calculation</span>
      </div>

      {/* Total Achievement Score label */}
      <div style={{ background: "var(--bg-surface-raised)", padding: "8px 16px", textAlign: "center", borderBottom: "1px solid var(--border-section)" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Total Achievement Score</span>
      </div>

      {/* Top grid — 4 columns */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", background: "var(--bg-surface-raised)", borderBottom: "1px solid var(--border-section)" }}>
        {[
          { label: "Revenue of homes closed", prefix: "$", editable: true,
            name: "bonusCalculation.totalAchievementScore.revenueOfHomesClosed",
            value: fmt(data.totalAchievementScore.revenueOfHomesClosed) || "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e, { type: "number", maxLength: 11, minLength: 1 }) },
          { label: "Override percentage", prefix: null, editable: false,
            name: "bonusCalculation.totalAchievementScore.overridePercentage",
            value: data.totalAchievementScore.overridePercentage || "0.0008" },
          { label: "Bonus Pool Available at 100%", prefix: "$", editable: false,
            name: "bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100",
            value: fmt(data.totalAchievementScore.bonusPoolAvailableAt100) },
          { label: "Total Max at 120%", prefix: "$", editable: false,
            name: "bonusCalculation.totalAchievementScore.totalMaxAt120",
            value: fmt(data.totalAchievementScore.totalMaxAt120) },
        ].map((col, i) => (
          <div key={i} style={{ padding: "10px 12px", borderLeft: i > 0 ? "1px solid var(--border-section)" : "none" }}>
            <div className="sf-calc-label">{col.label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {col.prefix && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{col.prefix}</span>}
              <input type="text" name={col.name} value={col.value}
                readOnly={!col.editable} disabled={col.editable ? !isEditable : undefined}
                placeholder={(col as any).placeholder} onFocus={col.editable ? e => e.target.select() : undefined}
                onChange={col.editable ? (col as any).onChange : undefined}
                className={col.editable ? "sf-input" : "sf-input-ro"}
                style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Bottom row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "var(--bg-surface-raised)" }}>
        {/* Left: Total Score / % / Bonus Pool */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderRight: "1px solid var(--border-section)" }}>
          {[
            { label: "Total Score", name: "bonusCalculation.totalScore", value: data.totalScore, prefix: null },
            { label: "%", name: "bonusCalculation.percentage", value: data.percentage, prefix: null },
            { label: "Bonus Pool", name: "bonusCalculation.bonusPool", value: fmt(data.bonusPool), prefix: "$" },
          ].map((col, i) => (
            <div key={i} style={{ padding: "10px 12px", borderLeft: i > 0 ? "1px solid var(--border-section)" : "none", textAlign: "center" }}>
              <div className="sf-calc-label">{col.label}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                {col.prefix && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{col.prefix}</span>}
                <input type="text" readOnly name={col.name} value={col.value}
                  className="sf-input-ro" style={{ flex: 1, padding: "5px 8px", textAlign: "right", fontSize: 13 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Right: Total Bonus Payout */}
        <div style={{ padding: "12px 16px", background: "var(--bg-chip)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Total Bonus Payout</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, color: "var(--text-muted)" }}>$</span>
            <input type="text" readOnly name="bonusCalculation.totalBonusPayout"
              value={fmt(data.totalBonusPayout) || "0"}
              style={{ background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", width: 160 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusCalculationCM3PMSubForm;